import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { getPartyTeam } from "../../../../services/contacts";

// Helper function to check if body is empty
const isBodyEmpty = (body) => {
  if (!body) return true;
  if (Array.isArray(body)) return body.length === 0;
  if (typeof body === 'object') return Object.keys(body).length === 0;
  return false;
};

const DealershipTeamContext = createContext();

const initialState = {
  teamMembers: [],
  partyUuid: null,
  loading: false,
  error: null,
  isEmpty: false,
};

export const DealershipTeamProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const fetchRequestIdRef = useRef(0);

  const fetchTeamMembers = useCallback(
    async (partyUuid) => {
      const requestId = ++fetchRequestIdRef.current;

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        isEmpty: false,
      }));

      try {
        const response = await getPartyTeam(partyUuid);

        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        const responseStatusCode = response?.statusCode;
        const isValidResponse = responseStatusCode === 200 || responseStatusCode === 201;

        if (!isValidResponse) {
          const errorMessage = response?.msgCode?.[0]?.description || 'Invalid response status code';
          throw new Error(errorMessage);
        }

        const body = response?.body;
        
        if (isBodyEmpty(body)) {
          setState({
            teamMembers: [],
            partyUuid,
            loading: false,
            error: null,
            isEmpty: true,
          });
        } else {
          const members = Array.isArray(body) ? body : [];
          setState({
            teamMembers: members,
            partyUuid,
            loading: false,
            error: null,
            isEmpty: false,
          });
        }
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        console.error("Error fetching team members:", error);
        setState({
          teamMembers: [],
          partyUuid: null,
          loading: false,
          error,
          isEmpty: false,
        });
      }
    },
    []
  );

  const resetTeamMembers = useCallback(() => {
    setState(initialState);
    fetchRequestIdRef.current = 0;
  }, []);

  const value = {
    teamMembers: state.teamMembers,
    partyUuid: state.partyUuid,
    loading: state.loading,
    error: state.error,
    isEmpty: state.isEmpty,
    fetchTeamMembers,
    resetTeamMembers,
  };

  return (
    <DealershipTeamContext.Provider value={value}>
      {children}
    </DealershipTeamContext.Provider>
  );
};

export const useDealershipTeam = () => {
  const context = useContext(DealershipTeamContext);
  if (!context) {
    throw new Error("useDealershipTeam must be used within DealershipTeamProvider");
  }
  return context;
};

